/**
 * Sharing domain API — Share Links (Spec 014) (Spec 059, E2). Moved bitgleich from the former
 * monolithic `api.ts` — no behavior change (E5).
 */
import type { ApiCore } from './core';

/** Visibility of a share link (Spec 014 E4). */
export type ShareLinkVisibility = 'public' | 'authenticated';

/** Request body for POST documents/{id}/share-links (Spec 014 E3, E5). */
export interface CreateShareLinkRequest {
  visibility: ShareLinkVisibility;
  /** UI options: 7 / 30 / custom (in days). */
  expiresInDays?: number;
  /** Absolute UTC ISO-string; takes precedence over expiresInDays. */
  expiresAt?: string;
}

/** Response from POST documents/{id}/share-links (201) and GET documents/{id}/share-links entries. */
export interface ShareLinkDto {
  id: string;
  token: string;
  visibility: ShareLinkVisibility;
  /** ISO-8601 string from JSON (typed as Date to allow test fixture casts). */
  expiresAt: string | Date;
  createdAt: string | Date;
}

/** Entry in the Settings → Freigabe overview (GET /api/v1/share-links). */
export interface ShareLinkOverviewDto {
  id: string;
  token: string;
  documentId: string;
  documentTitle: string;
  visibility: ShareLinkVisibility;
  /** ISO-8601 string from JSON (typed as Date to allow test fixture casts). */
  expiresAt: string | Date;
  createdAt: string | Date;
}

/**
 * Public document DTO (GET /api/v1/share/{token}).
 * ADR-049 compliant: no storageUri, no ocrText.
 * Index signature allows runtime-safety checks (e.g. assert absence of forbidden fields).
 */
export interface SharedDocumentDto {
  [key: string]: unknown;
  documentId: string;
  title: string;
  fileName: string;
  subject?: string | null;
  category?: string | null;
  subCategory?: string | null;
  documentDate?: string | null;
  /** ISO-8601 string from JSON (typed as Date to allow test fixture casts). */
  uploadDate: string | Date;
  fileSize: number;
  mimeType: string;
  /** Relative download path, e.g. "share/{token}/content" — never a gs:// URI (ADR-049). */
  contentPath: string;
  /**
   * Only meaningful when the viewer is authenticated (Spec 027 E1, E3): true when they are also
   * on the deployment's email whitelist, i.e. already have full app access. Anonymous (Public-
   * link) responses always carry false.
   */
  viewerCanAccessFull?: boolean;
}

/** Machine-readable reason in HTTP 410 Gone response body (Spec 014 E6, E11). */
export type ShareLinkGoneReason = 'expired' | 'revoked';

/** Body of a 410 Gone response from the public share endpoint. */
export interface ShareLinkGoneResponse {
  reason: ShareLinkGoneReason;
  detail: string;
}

export class SharingApi {
  constructor(private readonly core: ApiCore) {}

  /**
   * Creates a new share link for a document (authenticated).
   * POST /api/v1/documents/{documentId}/share-links → 201 ShareLinkDto.
   * Spec 014 E3, E5.
   */
  async createShareLink(
    documentId: string,
    request: CreateShareLinkRequest,
  ): Promise<ShareLinkDto> {
    return this.core.fetchJson<ShareLinkDto>(
      `${this.core.baseUrl}/api/v1/documents/${documentId}/share-links`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.core.getAuthHeaders() as Record<string, string>),
        },
        body: JSON.stringify(request),
      },
    );
  }

  /**
   * Lists the active share links for a document (authenticated).
   * GET /api/v1/documents/{documentId}/share-links → 200 ShareLinkDto[].
   * Spec 014 E3.
   */
  async listShareLinks(documentId: string): Promise<ShareLinkDto[]> {
    return this.core.fetchJson<ShareLinkDto[]>(
      `${this.core.baseUrl}/api/v1/documents/${documentId}/share-links`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  /**
   * Revokes (soft-deletes) a share link (authenticated).
   * DELETE /api/v1/documents/{documentId}/share-links/{linkId} → 204.
   * Spec 014 E3.
   */
  async revokeShareLink(documentId: string, linkId: string): Promise<void> {
    return this.core.fetchVoid(
      `${this.core.baseUrl}/api/v1/documents/${documentId}/share-links/${linkId}`,
      {
        method: 'DELETE',
        headers: this.core.getAuthHeaders(),
      },
    );
  }

  /**
   * Overview of all active share links across all documents (authenticated).
   * GET /api/v1/share-links → 200 ShareLinkOverviewDto[].
   * Used by Settings → Freigabe tab (Spec 014 E3, E8).
   */
  async listAllShareLinks(): Promise<ShareLinkOverviewDto[]> {
    return this.core.fetchJson<ShareLinkOverviewDto[]>(`${this.core.baseUrl}/api/v1/share-links`, {
      headers: this.core.getAuthHeaders(),
    });
  }

  /**
   * Fetches the public document display DTO. No auth header — public endpoint.
   * GET /api/v1/share/{token} → 200 SharedDocumentDto | 401 | 403 | 404 | 410.
   * Spec 014 E3, E9. ADR-049: response contains no storageUri, no ocrText.
   */
  async getSharedDocument(token: string): Promise<SharedDocumentDto> {
    return this.core.fetchJson<SharedDocumentDto>(
      `${this.core.baseUrl}/api/v1/share/${token}`,
      // [AllowAnonymous] endpoint — but send the Bearer when logged in so an
      // Authenticated-visibility link can succeed (Spec 015 E1). Harmless for
      // Public links, which ignore the header server-side.
      { headers: this.core.getAuthHeaders() },
      // Suppress the global error handler AND the 401 auth-redirect bounce: this is
      // a public page that renders its own "please log in" state instead of a
      // session-expired logout (Spec 015 E2).
      true,
      true,
    );
  }

  /**
   * Returns the shareable frontend URL for a token (clipboard, Spec 014 S13b).
   * This is the URL that gets copied to clipboard when sharing.
   */
  getSharedDownloadUrl(token: string): string {
    // ponytail: clipboard URL = FE route, not the backend API URL (S13b)
    return `${window.location.origin}/share/${token}`;
  }

  /**
   * Resolves a share-link's document content as a signed GCS URL, with an authenticated request.
   * GET /api/v1/share/{token}/content-url → 200 { url }.
   * Spec 054 fix (E6): a plain `<a href download>` browser navigation never carried the
   * Authorization header, so an Authenticated-link download 401'd even for a logged-in,
   * non-provisioned user. Direction (a) — `fetch()`-ing the legacy `/content` 302 into a Blob —
   * was reverted: that redirect targets a signed GCS URL, and `fetch()` follows it cross-origin,
   * which is CORS-blocked (the document bucket has no CORS config). This endpoint instead returns
   * the signed URL as JSON; the caller navigates the browser there (navigation ignores CORS).
   * Same header treatment as getSharedDocument (Spec 015 E1/E2): send the Bearer when logged in,
   * harmless for Public links; suppress the global error handler AND the 401 auth-redirect
   * bounce — this is a public page with its own "please log in" state.
   */
  async getSharedContentUrl(token: string): Promise<string> {
    const dto = await this.core.fetchJson<{ url: string }>(
      `${this.core.baseUrl}/api/v1/share/${token}/content-url`,
      { headers: this.core.getAuthHeaders() },
      true,
      true,
    );
    return dto.url;
  }
}
